import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ROUTES } from "../../../routes/routes.constants";
import CommonTable from "../../../common_components/CommonTable";
import Pagination from "../../../common_components/Pagination";
import TableToolbar from "../../../common_components/TableToolbar";
import { ExportButton } from "../../../common_components/FormInputs";
import { SUMMARY_ROW_POSITIONS, TABLE_TYPES, EXPAND_TRIGGERS } from "../../../common_components/tableConstants";
import DiffChip from "./DiffChip";
import AmBadges from "./AmBadges";
import { commissionTableContainerSx, CurrencyText, SubCurrencyText } from "./commissionTableShared";
const ITEMS_PER_PAGE = 10;
// ── Grouped columns (Summary layout) ──
const columns = [
    { id: "client", label: "Client", width: "180px", align: "left", sortable: true },
    { id: "am", label: "AM", width: "80px", align: "center", sortable: false },
    {
        id: "commissionGroup",
        label: "Commission",
        align: "center",
        children: [
            { id: "initial_projected", label: "Initial Projected", width: "170px", align: "left", sortable: true },
            { id: "actual_projected", label: "Actual/Projected", width: "170px", align: "left", sortable: true },
            { id: "current", label: "Current", width: "150px", align: "left", sortable: true },
            { id: "diff_percent", label: "Diff(%)", width: "180px", align: "left", sortable: false },
            { id: "diff_amount", label: "Diff($)", width: "170px", align: "left", sortable: true },
        ],
    },
    {
        id: "expectedCommissionGroup",
        label: "Expected Commission",
        align: "center",
        children: [
            { id: "days_commission", label: "Day's Commission", width: "160px", align: "left", sortable: true },
        ],
    },
];
// Leaf columns the user is allowed to hide (Client stays pinned/visible).
const HIDEABLE_COLUMN_IDS = [
    "am",
    "initial_projected", "actual_projected", "current", "diff_percent", "diff_amount",
    "days_commission",
];
// Maps a column id to the raw sortable value on a row.
const SORT_ACCESSORS = {
    client: (r) => r.client.toLowerCase(),
    initial_projected: (r) => r.initialProjected.primary,
    actual_projected: (r) => r.actualProjected.primary,
    current: (r) => r.current.primary,
    diff_amount: (r) => r.diffAmount.primary,
    days_commission: (r) => r.daysCommission,
};
// A metric cell: bold value + diff chip on top, muted secondary value below.
// When compare is off, only the primary value is shown (no chip, no secondary).
const DualValueView = ({ cell, bold, compareOn = true }) => (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }, children: [cell.primary !== 0 ? _jsx(CurrencyText, { value: cell.primary, bold: bold }) : "-", compareOn && cell.diff.percentage !== 0 && _jsx(DiffChip, { percentage: cell.diff.percentage, trend: cell.diff.trend, strong: bold })] }), compareOn && cell.secondary !== 0 && _jsx(SubCurrencyText, { value: cell.secondary })] }));
// The Diff(%) cell: selected-month chip, plus the compare-month chip when compare is on.
const DualDiffView = ({ cell, bold, compareOn = true }) => (_jsxs(Box, { sx: { px: 1, display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(DiffChip, { percentage: cell.left.percentage, trend: cell.left.trend, strong: bold }), compareOn && _jsx(DiffChip, { percentage: cell.right.percentage, trend: cell.right.trend, strong: bold })] }));
const SummaryTab = ({ rows, totals, compareOn = true, onExport, exporting, canOpenReport = true, range, primaryMonth }) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("initial_projected");
    const [sortOrder, setSortOrder] = useState("desc");
    const [search, setSearch] = useState("");
    // ── Column visibility ─────────────────────────────────────
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchorEl, setColumnVisibilityAnchorEl] = useState(null);
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q)
            return rows;
        return rows.filter((r) => r.client.toLowerCase().includes(q));
    }, [rows, search]);
    const filteredCount = filteredRows.length;
    React.useEffect(() => {
        setPage(1);
    }, [filteredCount]);
    const sortedRows = useMemo(() => {
        const accessor = SORT_ACCESSORS[sortBy];
        if (!accessor)
            return filteredRows;
        return [...filteredRows].sort((a, b) => {
            const va = accessor(a);
            const vb = accessor(b);
            if (va < vb)
                return sortOrder === "asc" ? -1 : 1;
            if (va > vb)
                return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredRows, sortBy, sortOrder]);
    const totalPages = Math.max(1, Math.ceil(sortedRows.length / ITEMS_PER_PAGE));
    const pagedRows = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return sortedRows.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedRows, page]);
    // ── Body cells ────────────────────────────────────────────
    const renderCell = (row, col) => {
        switch (col.id) {
            case "client":
                return (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1, px: 1, cursor: canOpenReport ? "pointer" : "default" }, onClick: canOpenReport ? () => {
                        sessionStorage.setItem("commission_tracker_client_id", row.id);
                        if (primaryMonth)
                            sessionStorage.setItem("commission_tracker_month", primaryMonth);
                        navigate(ROUTES.commissionTrackerReport.path, { state: { storeId: row.id, storeName: row.client, month: primaryMonth } });
                    } : undefined, children: [_jsx(Typography, { sx: { fontWeight: 400, color: "#27272a", fontSize: "14px !important", whiteSpace: "nowrap" }, children: row.client }), canOpenReport && row.clientLinked && _jsx("img", { src: "/assets/link.svg", alt: "link" })] }));
            case "am": return _jsx(Box, { sx: { px: 1 }, children: _jsx(AmBadges, { managers: row.am }) });
            case "initial_projected": return _jsx(DualValueView, { cell: row.initialProjected, compareOn: compareOn });
            case "actual_projected": return _jsx(DualValueView, { cell: row.actualProjected, compareOn: compareOn });
            case "current": return _jsx(DualValueView, { cell: row.current, compareOn: compareOn });
            case "diff_percent": return _jsx(DualDiffView, { cell: row.diffPercent, compareOn: compareOn });
            case "diff_amount": return _jsx(DualValueView, { cell: row.diffAmount, compareOn: compareOn });
            case "days_commission": return row.daysCommission ? _jsx(CurrencyText, { value: row.daysCommission }) : "-";
            default: return null;
        }
    };
    // ── Summary (footer) row ──────────────────────────────────
    const renderSummaryRow = (_rows, col) => {
        switch (col.id) {
            case "client":
                return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontWeight: 700, color: "#181d27", fontSize: "14px !important" }, children: "Total" }) });
            case "am": return "";
            case "initial_projected": return _jsx(DualValueView, { cell: totals.initialProjected, bold: true, compareOn: compareOn });
            case "actual_projected": return _jsx(DualValueView, { cell: totals.actualProjected, bold: true, compareOn: compareOn });
            case "current": return _jsx(DualValueView, { cell: totals.current, bold: true, compareOn: compareOn });
            case "diff_percent": return _jsx(DualDiffView, { cell: totals.diffPercent, bold: true, compareOn: compareOn });
            case "diff_amount": return _jsx(DualValueView, { cell: totals.diffAmount, bold: true, compareOn: compareOn });
            case "days_commission": return _jsx(CurrencyText, { value: totals.daysCommission, bold: true });
            default: return "";
        }
    };
    const showSummaryRow = sortedRows.length > 0;
    // Header range comes from the API (month-start → last synced day). Fallback to
    // month-start → yesterday only when the API didn't send one (e.g. empty data).
    const fallbackRange = useMemo(() => {
        const today = new Date();
        const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        const endDate = new Date(today);
        endDate.setDate(today.getDate() - 1);
        const fmt = (date) => date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
        return `${fmt(startDate)} - ${fmt(endDate)}`;
    }, []);
    // API sends "MM/DD/YYYY-MM/DD/YYYY"; add spaces around the separator for display.
    const displayRange = range ? range.replace("-", " - ") : fallbackRange;
    const currentMonth = new Date().toISOString().slice(0, 7);
    const isCurrentMonth = primaryMonth === currentMonth;
    return (_jsxs(Box, { sx: { bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "8px", overflow: "hidden" }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: isCurrentMonth ? "space-between" : "flex-end",
                    alignItems: "center",
                    pl: 2,
                    borderBottom: "1px solid #EAECF0",
                    bgcolor: "#fff",
                }, children: [isCurrentMonth && (_jsx("span", { style: { fontSize: '16px', marginTop: "8px", fontWeight: "600" }, children: displayRange })), _jsx(TableToolbar, { title: "", showSearch: true, searchValue: search, onSearchChange: setSearch, searchPlaceholder: "Search client...", showRows: true, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchorEl(e.currentTarget), showFullScreen: true, customActions: rows.length > 0 ? _jsx(ExportButton, { onClick: () => onExport?.(), loading: exporting }) : null })] }), _jsx(Box, { className: "table-container", sx: {
                    ...commissionTableContainerSx,
                    position: "relative",
                    borderRadius: 0,
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    // ── Compact rows to match Figma (62px: two-line content + ~8px padding). ──
                    // Prefixing the global cell selector with `&` adds one class, so this beats
                    // the global `.reusable-table-container … .reusable-table-cell` rule on
                    // specificity alone — no !important needed. Vertical padding only; the
                    // horizontal padding and column widths are left untouched.
                    "& .reusable-table-container .reusable-table .reusable-table-body .reusable-table-row .reusable-table-cell": {
                        height: "52px",
                        paddingTop: "6px",
                        paddingBottom: "6px",
                    },
                    "& .reusable-table-body tr.reusable-table-summary-row td": {
                        height: "52px",
                        paddingTop: "6px",
                        paddingBottom: "6px",
                    },
                }, children: _jsx(CommonTable, { columns: columns, rows: sortedRows, renderCell: renderCell, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (id, dir) => { setSortBy(id); setSortOrder(dir || (sortOrder === "asc" ? "desc" : "asc")); }, expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, enableColumnFilters: false, enableColumnVisibility: false, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchorEl, onColumnVisibilityClose: () => setColumnVisibilityAnchorEl(null), columnVisibilityConfig: { enable: true, columns: HIDEABLE_COLUMN_IDS, enableReorder: false }, enableSelection: false, enableSummaryRow: showSummaryRow, renderSummaryRow: showSummaryRow ? renderSummaryRow : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, type: TABLE_TYPES.GROUPED, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 140px)", emptyMessage: "No commission data available." }) }), _jsx(Box, { className: "user-list-pagination-wrapper", children: _jsx(Pagination
                // currentPage={page}
                // totalPages={totalPages}
                , { 
                    // currentPage={page}
                    // totalPages={totalPages}
                    currentPage: 1, totalPages: 1, onPageChange: setPage, maxVisiblePages: 5, totalEntries: sortedRows.length, itemsPerPage: ITEMS_PER_PAGE }) })] }));
};
export default SummaryTab;
